import { httpClient } from '@/shared/lib/http-client';
import { API_ENDPOINTS } from '@/shared/config/api';
import type {
  DoctorsResponse,
  DoctorResponse,
  DoctorMutationResponse,
  ImageUploadResponse,
  CreateDoctorData,
  UpdateDoctorData,
  DoctorSearchParams,
  DoctorSortOrderUpdate,
  BulkSortOrderUpdateResponse
} from '@/shared/types';

/**
 * API сервис для управления врачами
 * Содержит методы для CRUD операций с врачами и их изображениями
 */
export const doctorsApi = {
  /**
   * Получить список всех врачей
   * @param params - Параметры поиска и фильтрации
   * @returns Ответ со списком врачей
   */
  async getAll(params?: DoctorSearchParams): Promise<DoctorsResponse> {
    const response = await httpClient.get<DoctorsResponse>(
      API_ENDPOINTS.DOCTORS.LIST,
      { params }
    );
    return response.data;
  },

  /**
   * Получить врача по ID
   * @param id - ID врача
   * @returns Ответ с данными врача
   */
  async getById(id: number): Promise<DoctorResponse> {
    const response = await httpClient.get<DoctorResponse>(
      API_ENDPOINTS.DOCTORS.DETAIL(id)
    );
    return response.data;
  },

  /**
   * Создать нового врача
   * @param data - Данные для создания врача
   * @returns Ответ с созданным врачом
   */
  async create(data: CreateDoctorData): Promise<DoctorMutationResponse> {
    const response = await httpClient.post<DoctorMutationResponse>(
      API_ENDPOINTS.DOCTORS.CREATE,
      data
    );
    return response.data;
  },

  /**
   * Обновить данные врача
   * @param id - ID врача
   * @param data - Данные для обновления
   * @returns Ответ с обновленным врачом
   */
  async update(
    id: number,
    data: UpdateDoctorData
  ): Promise<DoctorMutationResponse> {
    const response = await httpClient.patch<DoctorMutationResponse>(
      API_ENDPOINTS.DOCTORS.UPDATE(id),
      data
    );
    return response.data;
  },

  /**
   * Обновить статус активности врача
   * @param id - ID врача
   * @param isActive - Новый статус активности
   * @returns Ответ с обновленным врачом
   */
  async updateStatus(
    id: number,
    isActive: boolean
  ): Promise<DoctorMutationResponse> {
    const response = await httpClient.patch<DoctorMutationResponse>(
      API_ENDPOINTS.DOCTORS.UPDATE_STATUS(id),
      { isActive }
    );
    return response.data;
  },

  /**
   * Загрузить изображение врача
   * @param id - ID врача
   * @param file - Файл изображения
   * @param onUploadProgress - Функция отслеживания прогресса загрузки
   * @returns Ответ с информацией о загруженном изображении
   */
  async uploadImage(
    id: number,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ImageUploadResponse> {
    const response = await httpClient.uploadFile<ImageUploadResponse>(
      API_ENDPOINTS.DOCTORS.UPLOAD_IMAGE(id),
      file,
      'image',
      onUploadProgress
    );
    return response.data;
  },

  /**
   * Установить активное изображение врача
   * @param id - ID врача
   * @param imageId - ID изображения
   * @returns Ответ с обновленным врачом
   */
  async setActiveImage(
    id: number,
    imageId: number
  ): Promise<DoctorMutationResponse> {
    const response = await httpClient.patch<DoctorMutationResponse>(
      API_ENDPOINTS.DOCTORS.SET_ACTIVE_IMAGE(id, imageId)
    );
    return response.data;
  },

  /**
   * Удалить изображение врача
   * @param id - ID врача
   * @param imageId - ID изображения
   * @returns Ответ с подтверждением удаления
   */
  async deleteImage(
    id: number,
    imageId: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await httpClient.delete<{
      success: boolean;
      message: string;
    }>(API_ENDPOINTS.DOCTORS.DELETE_IMAGE(id, imageId));
    return response.data;
  },

  /**
   * Массовое обновление порядка сортировки врачей
   * @param updates - Массив обновлений с ID врача и новым sortOrder
   * @returns Ответ с результатом обновления
   */
  async updateSortOrder(
    updates: DoctorSortOrderUpdate[]
  ): Promise<BulkSortOrderUpdateResponse> {
    const response = await httpClient.patch<BulkSortOrderUpdateResponse>(
      API_ENDPOINTS.DOCTORS.UPDATE_SORT_ORDER,
      { updates }
    );
    return response.data;
  }
};
